import type { FC } from 'react'
import { format, parseISO } from 'date-fns'
import { Box, Paper, Stack, Typography, alpha } from '@mui/material'
import BlockRounded from '@mui/icons-material/BlockRounded'
import CheckRounded from '@mui/icons-material/CheckRounded'
import {
  bffCrmOnboardingTimeline,
  type BffCrmOnboardingTimelineRetrieveResponse,
} from '@fireflyhealth/api'
import { useQuery } from '~/shared/hooks/query'
import { Loader } from '~/shared/ui'
import { ONBOARDING_STATES } from '~/entities/person'

type TimelineItem = BffCrmOnboardingTimelineRetrieveResponse['items'][number]

interface OnboardingTimelineProps {
  personId: number
  /** Render only the rail — the design-refresh tab provides its own card
   * chrome (header + status pill) around it. */
  hideChrome?: boolean
}

/** Width of the spine column (px). Both milestone and step nodes are centered here. */
const SPINE_COL = 40
/** Diameter of the milestone node (px). */
const MILESTONE_SIZE = 28
/** Diameter of the step node (px). */
const STEP_SIZE = 12
/** Width of the connecting spine line (px). */
const LINE_W = 2

const formatTimestamp = (completedAt: string | null) => {
  if (!completedAt) return null
  const at = parseISO(completedAt)
  return `${format(at, 'MMM d, yyyy')} · ${format(at, 'h:mm a')}`
}

// Milestones are status markers, not actionable items. Visual tone signals
// the nature of the milestone: `success` for reached positive states
// (initialized, signedup), `error` for the terminal deactivated state where
// the member can no longer access the app, and `pending` for a milestone the
// member has not yet reached. The tone is computed by the BFF and arrives on
// each milestone item.
type MilestoneTone = NonNullable<TimelineItem['tone']>

const MilestoneNode: FC<{ tone: MilestoneTone }> = ({ tone }) => {
  if (tone === 'success') {
    return (
      <Box
        sx={theme => ({
          width: MILESTONE_SIZE,
          height: MILESTONE_SIZE,
          borderRadius: '50%',
          bgcolor: 'success.main',
          color: 'common.white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: `0 0 0 4px ${alpha(theme.palette.success.main, 0.12)}`,
        })}
      >
        <CheckRounded sx={{ fontSize: MILESTONE_SIZE * 0.6 }} />
      </Box>
    )
  }
  if (tone === 'error') {
    return (
      <Box
        sx={theme => ({
          width: MILESTONE_SIZE,
          height: MILESTONE_SIZE,
          borderRadius: '50%',
          bgcolor: 'error.main',
          color: 'common.white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: `0 0 0 4px ${alpha(theme.palette.error.main, 0.14)}`,
        })}
      >
        <BlockRounded sx={{ fontSize: MILESTONE_SIZE * 0.6 }} />
      </Box>
    )
  }
  return (
    <Box
      sx={theme => ({
        width: MILESTONE_SIZE,
        height: MILESTONE_SIZE,
        borderRadius: '50%',
        bgcolor: 'background.paper',
        border: `2px solid ${theme.palette.grey[300]}`,
        flexShrink: 0,
      })}
    />
  )
}

const StepNode: FC<{ isCompleted: boolean; isBlocked?: boolean }> = ({
  isCompleted,
  isBlocked,
}) => {
  // A blocked step is the step the member is currently stuck on; mirror the
  // deactivated milestone's error tone (red fill + BlockRounded glyph).
  if (isBlocked) {
    return (
      <Box
        sx={theme => ({
          width: STEP_SIZE,
          height: STEP_SIZE,
          borderRadius: '50%',
          bgcolor: 'error.main',
          color: 'common.white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.14)}`,
        })}
      >
        <BlockRounded sx={{ fontSize: STEP_SIZE * 0.75 }} />
      </Box>
    )
  }
  if (isCompleted) {
    return (
      <Box
        sx={{
          width: STEP_SIZE,
          height: STEP_SIZE,
          borderRadius: '50%',
          bgcolor: 'success.main',
          flexShrink: 0,
        }}
      />
    )
  }
  return (
    <Box
      sx={theme => ({
        width: STEP_SIZE,
        height: STEP_SIZE,
        borderRadius: '50%',
        bgcolor: 'background.paper',
        border: `1.5px solid ${theme.palette.grey[400]}`,
        flexShrink: 0,
      })}
    />
  )
}

const MoreStepsHint: FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'stretch' }} data-testid="onboarding-more-steps-hint">
    <Box
      sx={{
        width: SPINE_COL,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box sx={{ width: LINE_W, flex: 1, bgcolor: 'divider' }} />
    </Box>
    <Box sx={{ flex: 1, py: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
        More steps unlock as the member progresses
      </Typography>
    </Box>
  </Box>
)

const MilestoneRow: FC<{
  item: TimelineItem
  isFirst: boolean
  isLast: boolean
}> = ({ item, isFirst, isLast }) => {
  const isCompleted = item.is_completed
  // `tone` is always set by the BFF for milestone items; fall back to
  // `pending` only to satisfy the schema's nullable typing (steps don't reach
  // this row).
  const tone: MilestoneTone = item.tone ?? 'pending'
  const timestampLabel = formatTimestamp(item.completed_at)
  // Extra breathing room above a milestone is baked into the spine connector
  // (rather than a margin-top) so the vertical line stays continuous from the
  // preceding step into the milestone.
  const topConnectorHeight = isFirst ? 12 : 28
  // The connector going INTO the milestone takes the milestone's tone so the
  // transition (e.g. green spine → red deactivated node) reads as continuous.
  const topConnectorColor = isFirst
    ? 'transparent'
    : tone === 'error'
      ? 'error.light'
      : isCompleted
        ? 'success.light'
        : 'divider'
  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
      <Box
        sx={{
          width: SPINE_COL,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: LINE_W,
            height: topConnectorHeight,
            flexShrink: 0,
            bgcolor: topConnectorColor,
          }}
        />
        <MilestoneNode tone={tone} />
        <Box
          sx={{
            width: LINE_W,
            flex: 1,
            minHeight: 12,
            // The spine should terminate at the final milestone — no trailing
            // line below it.
            bgcolor: isLast ? 'transparent' : isCompleted ? 'success.light' : 'divider',
          }}
        />
      </Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          flex: 1,
          // Align the label with the milestone circle's vertical center by
          // offsetting the row by the top connector height and capping the
          // intrinsic height to the milestone size (otherwise the parent's
          // `alignItems: stretch` makes this Stack span the full row including
          // the bottom connector, pushing the text above the circle).
          alignSelf: 'flex-start',
          mt: `${topConnectorHeight}px`,
          height: MILESTONE_SIZE,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            lineHeight: 1.2,
            color: tone === 'error' ? 'error.main' : 'text.primary',
          }}
        >
          {item.display_name}
        </Typography>
        {timestampLabel && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {timestampLabel}
          </Typography>
        )}
      </Stack>
    </Box>
  )
}

const StepRow: FC<{ item: TimelineItem }> = ({ item }) => {
  const isCompleted = item.is_completed
  const blockedReasonLabel = item.blocked_reason?.display_label ?? null
  const isBlocked = !!blockedReasonLabel
  // The blocked step replaces the neutral pending-step connector with an
  // error-toned one so the eye is drawn down the spine to the red node, while
  // the connector below stays neutral.
  const topConnectorColor = isBlocked ? 'error.light' : isCompleted ? 'success.light' : 'divider'
  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
      <Box
        sx={{
          width: SPINE_COL,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: LINE_W,
            height: 6,
            flexShrink: 0,
            bgcolor: topConnectorColor,
          }}
        />
        <StepNode isCompleted={isCompleted} isBlocked={isBlocked} />
        <Box
          sx={{
            width: LINE_W,
            flex: 1,
            minHeight: 6,
            bgcolor: isCompleted ? 'success.light' : 'divider',
          }}
        />
      </Box>
      <Stack
        direction="column"
        spacing={0.25}
        sx={{
          flex: 1,
          py: 0.5,
          borderRadius: 1,
          transition: 'background-color 120ms ease',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: 13,
            color: isBlocked ? 'error.main' : isCompleted ? 'text.secondary' : 'text.primary',
            fontWeight: isBlocked ? 600 : 'inherit',
            lineHeight: 1.4,
          }}
        >
          {item.display_name}
        </Typography>
        {blockedReasonLabel && (
          <Typography
            variant="caption"
            data-testid="onboarding-signup-block-reason"
            sx={{ color: 'error.main', fontStyle: 'italic', lineHeight: 1.3, fontWeight: 500 }}
          >
            {blockedReasonLabel}
          </Typography>
        )}
      </Stack>
    </Box>
  )
}

const OnboardingTimeline: FC<OnboardingTimelineProps> = ({ personId, hideChrome }) => {
  const {
    data,
    isPending: isLoading,
    isError,
  } = useQuery(
    bffCrmOnboardingTimeline.retrieveQueryKey({ person_id: personId }),
    () => bffCrmOnboardingTimeline.retrieve({ person_id: personId }).then(r => r.data),
    { staleTime: 0, refetchOnMount: 'always', enabled: !!personId },
  )

  if (isLoading) return <Loader />
  if (isError || !data) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography sx={{ color: 'error.main' }}>Failed to load onboarding timeline.</Typography>
      </Box>
    )
  }

  const { items, show_initialized_hint: showInitializedHint } = data

  const rail = items.map((item, index) => {
    const isFirst = index === 0
    const isLast = index === items.length - 1

    if (item.kind === 'milestone') {
      const milestone = (
        <MilestoneRow
          key={`${item.kind}-${item.key}`}
          item={item}
          isFirst={isFirst}
          isLast={isLast}
        />
      )
      // Render the "more screens unlock" hint between the last initialized
      // step and the signedup milestone, only while the member is still
      // in the initialized phase.
      const isSignedupMilestone = item.key === ONBOARDING_STATES.signedup
      if (isSignedupMilestone && showInitializedHint) {
        return (
          <Box key={`group-${item.key}`}>
            <MoreStepsHint />
            {milestone}
          </Box>
        )
      }
      return milestone
    }

    return <StepRow key={`${item.kind}-${item.key}-${index}`} item={item} />
  })

  if (hideChrome) {
    return <Box data-testid="onboarding-timeline">{rail}</Box>
  }

  return (
    <Paper
      elevation={0}
      data-testid="onboarding-timeline"
      sx={theme => ({
        p: 3,
        m: 2,
        // Literal: sx numbers multiply theme.shape.borderRadius, which is 12
        // (4 * 12 = 48px).
        borderRadius: '16px',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
      })}
    >
      <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
        Onboarding Progress
      </Typography>

      {rail}
    </Paper>
  )
}

export default OnboardingTimeline
